/**
 * EV driver GPS tracker: ping drivertir every minute from any page.
 * The driver id is the same clientUiId used by charging sessions and order_220.
 * Falls back to cookie location or IP-only ping when GPS is unavailable.
 */
import { readOrCreateClientId } from './clientId';
import { getCookie } from './cookiesManager';

const DRIVERTIR_URL = (
  (process.env.REACT_APP_DRIVERTIR_URL && String(process.env.REACT_APP_DRIVERTIR_URL).trim()) ||
  'https://220-km.com:8090'
).replace(/\/$/, '');

const QUEUE_KEY = 'evDriverTrackerQueue';
const FLUSH_INTERVAL_MS = 60_000;
const MAX_QUEUE = 500;
const BATCH_SIZE = 20;

const TRACKER_URL = `${DRIVERTIR_URL}/api/ev-driver-tracker/points`;

let started = false;

function getDriverId() {
  // Same id the charging UI sends as clientUiId, so GPS rows join to order_220.
  return readOrCreateClientId();
}

function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(queue) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE)));
  } catch {
    // ignore quota errors
  }
}

function enqueuePoint(point) {
  const queue = readQueue();
  queue.push(point);
  writeQueue(queue);
  if (queue.length >= BATCH_SIZE) {
    flushQueue();
  }
}

async function flushQueue() {
  const queue = readQueue();
  if (!queue.length) return;

  const batch = queue.slice(0, BATCH_SIZE);
  const driverId = getDriverId();
  const payload = {
    driverId,
    points: batch.map(p => ({
      lat: p.lat,
      lng: p.lng,
      ts: p.ts,
      source: p.source,
      accuracyM: p.accuracyM,
    })),
  };

  try {
    const res = await fetch(TRACKER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
    if (!res.ok) return;
    const remaining = queue.slice(batch.length);
    writeQueue(remaining);
  } catch {
    // keep queue for retry
  }
}

function cookieLocation() {
  const saved = getCookie('userLocation');
  if (saved?.latitude != null && saved?.longitude != null) {
    return { lat: Number(saved.latitude), lng: Number(saved.longitude) };
  }
  return null;
}

function gpsLocationIfGranted() {
  return new Promise(resolve => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    const onSuccess = pos => {
      resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracyM: pos.coords.accuracy,
        source: 'gps',
      });
    };
    const onError = () => resolve(null);

    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then(result => {
          if (result.state !== 'granted') {
            resolve(null);
            return;
          }
          navigator.geolocation.getCurrentPosition(onSuccess, onError, {
            enableHighAccuracy: false,
            maximumAge: 60_000,
            timeout: 8_000,
          });
        })
        .catch(() => resolve(null));
      return;
    }

    navigator.geolocation.getCurrentPosition(onSuccess, onError, {
      enableHighAccuracy: false,
      maximumAge: 60_000,
      timeout: 8_000,
    });
  });
}

async function collectAndEnqueuePing() {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

  const ts = Date.now();
  const gps = await gpsLocationIfGranted();
  if (gps) {
    enqueuePoint({
      lat: gps.lat,
      lng: gps.lng,
      ts,
      source: 'gps',
      accuracyM: gps.accuracyM,
    });
    return;
  }

  const cookie = cookieLocation();
  if (cookie) {
    enqueuePoint({
      lat: cookie.lat,
      lng: cookie.lng,
      ts,
      source: 'cookie',
    });
    return;
  }

  enqueuePoint({ ts, source: 'ip' });
}

/** High-fidelity point from map track recording (already noise-filtered). */
export function enqueueTrackPoint({ lat, lng, ts, source = 'gps', accuracyM }) {
  enqueuePoint({
    lat,
    lng,
    ts: ts || Date.now(),
    source,
    accuracyM,
  });
}

export function startEvDriverTracker() {
  if (started || typeof window === 'undefined') return;
  started = true;

  collectAndEnqueuePing();
  window.setInterval(collectAndEnqueuePing, FLUSH_INTERVAL_MS);
  window.setInterval(flushQueue, FLUSH_INTERVAL_MS);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      collectAndEnqueuePing();
    }
  });

  window.addEventListener('pagehide', () => {
    flushQueue();
  });
}
